import { useEffect, useState } from 'react';
import { useExportGate } from '../hooks/useExportGate';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Trash2, Edit2, Check, X, Send, Clock, AlertCircle, IndianRupee, Calendar, Eye, Download, FileSpreadsheet, ArrowRight, Users, Building2, Share2, ExternalLink, Percent, Lock } from 'lucide-react';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import QuotationReview from '@/components/QuotationReview';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-600', icon: Send },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-600', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-600', icon: Check },
  partially_accepted: { label: 'Partial', color: 'bg-amber-100 text-amber-600', icon: Check },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600', icon: X },
  revision_requested: { label: 'Revision Requested', color: 'bg-purple-100 text-purple-600', icon: Edit2 },
  expired: { label: 'Expired', color: 'bg-orange-100 text-orange-600', icon: AlertCircle },
};

export default function Quotations() {
  const { exportLocked, guardExport } = useExportGate();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // SECURITY: Only owner/staff can access the main Quotations management page
  // Clients should use /client/quotations, Vendors should use /vendor/quotations-received
  const isAuthorized = ['owner', 'staff', 'admin', 'project_manager'].includes(user.role);
  
  const [quotations, setQuotations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotationType, setQuotationType] = useState('all'); // 'all', 'client', 'vendor'
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // P2 NEW: Amend quotation state
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [amendReason, setAmendReason] = useState('');
  const [amending, setAmending] = useState(false);
  
  // Discount edit state
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountTarget, setDiscountTarget] = useState(null);
  const [discountEditValue, setDiscountEditValue] = useState(0);
  
  const [formData, setFormData] = useState({
    lead_id: '',
    project_id: '',
    items: [{ description: '', quantity: 1, unit: 'Nos', rate: 0, amount: 0 }],
    tax_percentage: 18,
    discount_percentage: 0,
    notes: '',
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });
  const isVendorRole = user.role === 'vendor';
  const isAdminRole = ['owner', 'staff', 'admin', 'project_manager'].includes(user.role);

  // Filter quotations by type (Admin only sees tabs, Vendor sees only their own)
  const filteredQuotations = quotations.filter(q => {
    // Vendors only see their own quotations - no type filtering needed
    if (isVendorRole) return true; // Backend already filters by vendor_id
    
    if (quotationType === 'all') return true;
    if (quotationType === 'vendor') return q.is_vendor_quotation || q.vendor_id;
    if (quotationType === 'client') return !q.is_vendor_quotation && !q.vendor_id;
    return true;
  });

  const vendorQuotations = quotations.filter(q => q.is_vendor_quotation || q.vendor_id);
  const clientQuotations = quotations.filter(q => !q.is_vendor_quotation && !q.vendor_id);

  // SECURITY: Redirect unauthorized users
  useEffect(() => {
    if (!isAuthorized) {
      toast.error('Access denied. This page is for internal use only.');
      if (user.role === 'client') {
        navigate('/client/quotations');
      } else if (user.role === 'vendor') {
        navigate('/vendor/quotations-received');
      } else {
        navigate('/');
      }
    }
  }, [isAuthorized, navigate, user.role]);

  useEffect(() => {
    if (!isAuthorized) return;
    fetchData();
  }, [isAuthorized]);

  const fetchData = async () => {
    try {
      const [quotRes, leadsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/quotations`),
        axios.get(`${API}/leads`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
      ]);
      setQuotations(quotRes.data);
      setLeads(leadsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'Nos', rate: 0, amount: 0 }],
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = subtotal * (formData.tax_percentage / 100);
    const discountAmount = subtotal * (formData.discount_percentage / 100);
    const total = subtotal + taxAmount - discountAmount;
    return { subtotal, taxAmount, discountAmount, total };
  };

  const handleCreate = async () => {
    if (!formData.items.length || formData.items.some(i => !i.description)) {
      toast.error('Please add at least one item with description');
      return;
    }

    try {
      await axios.post(`${API}/quotations`, {
        ...formData,
        valid_until: new Date(formData.valid_until).toISOString(),
      });
      toast.success('Quotation created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    }
  };

  const handleStatusChange = async (quotation, newStatus) => {
    try {
      await axios.put(`${API}/quotations/${quotation.id}`, { status: newStatus });
      toast.success(`Quotation marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim() || deleteReason.trim().length < 5) {
      toast.error('Please provide a reason for deletion (min 5 characters)');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/quotations/${selectedQuotation.id}`, {
        data: { reason: deleteReason.trim() }
      });
      toast.success('Quotation permanently deleted');
      setDeleteDialogOpen(false);
      setSelectedQuotation(null);
      setDeleteReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quotation');
    } finally {
      setDeleting(false);
    }
  };

  // P2 NEW: Amend quotation handler
  const handleAmend = async () => {
    if (!selectedQuotation) return;
    
    setAmending(true);
    try {
      const response = await axios.post(`${API}/quotations/${selectedQuotation.id}/amend`, {
        amendment_reason: amendReason || 'Updated quotation'
      });
      toast.success(response.data.message || 'Quotation amended successfully');
      setAmendDialogOpen(false);
      setSelectedQuotation(null);
      setAmendReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to amend quotation');
    } finally {
      setAmending(false);
    }
  };

  const handleConvertToInvoice = async (quotationId) => {
    try {
      const response = await axios.post(`${API}/invoices/from-quotation/${quotationId}`);
      toast.success(`Invoice ${response.data.invoice_number} created!`);
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  // Export quotation to PDF or Excel with authentication
  const handleExport = async (quotationId, format) => {
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}/export/${format}`, {
        responseType: 'blob'
      });
      
      // Get filename from response headers or generate one
      const quotation = quotations.find(q => q.id === quotationId);
      const filename = format === 'pdf' 
        ? `Quotation_${quotation?.quotation_number || quotationId}.pdf`
        : `Quotation_${quotation?.quotation_number || quotationId}.xlsx`;
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  // Update discount on quotation
  const handleDiscountSave = async () => {
    if (!discountTarget) return;
    try {
      await axios.put(`${API}/quotations/${discountTarget.id}`, {
        discount_percentage: parseFloat(discountEditValue) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Discount updated');
      setDiscountDialogOpen(false);
      fetchQuotations();
    } catch (error) {
      toast.error('Failed to update discount');
    }
  };

  // Preview PDF in new tab
  const handlePreviewPDF = async (quotationId) => {
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}/export/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to preview PDF');
    }
  };

  // Publish/Unpublish quotation to client with markup
  const handlePublishToClient = async (quotation, publish = true) => {
    try {
      const response = await axios.post(`${API}/quotations/${quotation.id}/publish-to-client`, {
        visible: publish
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client visibility');
    }
  };

  const resetForm = () => {
    setFormData({
      lead_id: '',
      project_id: '',
      items: [{ description: '', quantity: 1, unit: 'Nos', rate: 0, amount: 0 }],
      tax_percentage: 18,
      discount_percentage: 0,
      notes: '',
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead?.name || 'N/A';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'N/A';
  };

  const { subtotal, taxAmount, discountAmount, total } = calculateTotals();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="quotations-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-5xl font-bold tracking-tight text-stone-900 mb-2">
              {isVendorRole ? 'My Quotations' : 'Quotations'}
            </h1>
            <p className="text-stone-500">
              {isVendorRole ? 'Create and track quotations sent to Bella Casa Interiors' : 'Manage client proposals and estimates'}
            </p>
          </div>
          {/* Admin creates quotations here, Vendor redirects to rate card-based creation */}
          {isAdminRole && (
            <Button onClick={() => setCreateDialogOpen(true)} className="apple-button" data-testid="create-quotation-btn">
              <Plus size={18} className="mr-2" />
              New Quotation
            </Button>
          )}
          {isVendorRole && (
            <Button onClick={() => navigate('/vendor/quotations/create')} className="apple-button" data-testid="vendor-create-quotation-btn">
              <Plus size={18} className="mr-2" />
              Create Quotation
            </Button>
          )}
        </div>

        {/* Quotation Type Tabs - Only visible to Admin/Staff, NOT to Vendors */}
        {isAdminRole && (
          <Tabs value={quotationType} onValueChange={setQuotationType} className="w-full">
            <TabsList className="grid grid-cols-3 w-full max-w-md">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <FileText size={16} />
                All ({quotations.length})
              </TabsTrigger>
              <TabsTrigger value="client" className="flex items-center gap-2">
                <Building2 size={16} />
                To Client ({clientQuotations.length})
              </TabsTrigger>
              <TabsTrigger value="vendor" className="flex items-center gap-2">
                <Users size={16} />
                From Vendor ({vendorQuotations.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <FileText size={20} className="text-stone-800" />
              <span className="text-2xl font-bold">{filteredQuotations.length}</span>
            </div>
            <p className="text-sm text-stone-500">
              {quotationType === 'vendor' ? 'Vendor Quotations' : quotationType === 'client' ? 'Client Quotations' : 'Total Quotations'}
            </p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <Clock size={20} className="text-yellow-500" />
              <span className="text-2xl font-bold">{filteredQuotations.filter(q => q.status === 'pending' || q.status === 'draft').length}</span>
            </div>
            <p className="text-sm text-stone-500">Pending</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <Send size={20} className="text-blue-500" />
              <span className="text-2xl font-bold">{filteredQuotations.filter(q => q.status === 'sent').length}</span>
            </div>
            <p className="text-sm text-stone-500">Sent</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <Check size={20} className="text-green-500" />
              <span className="text-2xl font-bold">{filteredQuotations.filter(q => q.status === 'accepted' || q.status === 'partially_accepted').length}</span>
            </div>
            <p className="text-sm text-stone-500">Accepted</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <IndianRupee size={20} className="text-amber-600" />
              <span className="text-xl font-bold">₹{(filteredQuotations.reduce((sum, q) => sum + (q.total || 0), 0) / 100000).toFixed(1)}L</span>
            </div>
            <p className="text-sm text-stone-500">Total Value</p>
          </div>
        </div>

        {filteredQuotations.length === 0 ? (
          <div className="apple-card p-16 text-center">
            <FileText size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">
              {quotationType === 'vendor' ? 'No vendor quotations yet' : 
               quotationType === 'client' ? 'No client quotations yet' : 
               'No quotations yet'}
            </p>
            <p className="text-sm text-stone-400 mt-1">
              {quotationType === 'vendor' ? 'Vendor quotations will appear here' : 'Create your first quotation to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredQuotations.map((quot) => {
              const statusConfig = STATUS_CONFIG[quot.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusConfig.icon;
              const isVendorQuot = quot.is_vendor_quotation || quot.vendor_id;
              
              return (
                <div key={quot.id} className="apple-card p-6 group" data-testid={`quotation-${quot.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-manrope text-xl font-bold">{quot.quotation_number}</h3>
                        {/* Source Badge - Only visible to Admin, NOT to Vendors */}
                        {isAdminRole && (
                          isVendorQuot ? (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              <Users size={12} className="mr-1" /> From Vendor
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Building2 size={12} className="mr-1" /> To Client
                            </Badge>
                          )
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusConfig.color}`}>
                          <StatusIcon size={14} />
                          {statusConfig.label}
                        </span>
                        {quot.version > 1 && (
                          <Badge variant="outline" className="text-xs">v{quot.version}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                        {quot.lead_id && <span>Lead: {getLeadName(quot.lead_id)}</span>}
                        {quot.project_id && <span>Project: {getProjectName(quot.project_id)}</span>}
                        <span>Valid until: {formatDate(quot.valid_until)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs text-stone-500">Total</p>
                        <p className="font-manrope text-2xl font-bold">₹{quot.total?.toLocaleString()}</p>
                      </div>
                      
                      <div className="flex gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        {/* Edit Discount */}
                        {isAdminRole && (
                          <button
                            onClick={() => { setDiscountTarget(quot); setDiscountEditValue(quot.discount_percentage || 0); setDiscountDialogOpen(true); }}
                            className="p-2 text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                            title="Edit Discount"
                            data-testid={`edit-discount-quotation-${quot.id}`}
                          >
                            <Percent size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => { setSelectedQuotation(quot); setViewDialogOpen(true); }}
                          className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          data-testid={`view-quotation-${quot.id}`}
                        >
                          <Eye size={18} />
                        </button>

                        {/* Preview PDF in new tab */}
                        <button
                          onClick={() => guardExport(handlePreviewPDF)(quot.id)}
                          className="p-2 text-stone-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          title="Preview PDF"
                          data-testid={`preview-pdf-${quot.id}`}
                        >
                          <ExternalLink size={18} />
                        </button>
                        
                        {/* Export Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 text-stone-500 hover:text-green-600 hover:bg-green-50 rounded-lg">
                              <Download size={18} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(quot.id, 'pdf')}>
                              <FileText className="mr-2" size={14} />
                              Download PDF
                            {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                            <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(quot.id, 'excel')}>
                              <FileSpreadsheet className="mr-2" size={14} />
                              Download Excel
                            {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        
                        {quot.status === 'draft' && (
                          <button
                            onClick={() => handleStatusChange(quot, 'sent')}
                            className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                          >
                            <Send size={18} />
                          </button>
                        )}
                        {quot.status === 'sent' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(quot, 'accepted')}
                              className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg"
                            >
                              <Check size={18} />
                            </button>
                            <button
                              onClick={() => handleStatusChange(quot, 'rejected')}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                            >
                              <X size={18} />
                            </button>
                          </>
                        )}
                        {/* Convert to Invoice - for accepted quotations */}
                        {(quot.status === 'accepted' || quot.status === 'sent') && (
                          <Button 
                            size="sm"
                            className="apple-button"
                            onClick={() => handleConvertToInvoice(quot.id)}
                          >
                            <ArrowRight size={14} className="mr-1" />
                            Invoice
                          </Button>
                        )}
                        {/* Publish to Client - for admin only, shows on quotations with project */}
                        {isAdminRole && quot.project_id && (
                          quot.client_visible ? (
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => handlePublishToClient(quot, false)}
                              title="Click to unpublish from client"
                            >
                              <Share2 size={14} className="mr-1" />
                              Published
                            </Button>
                          ) : (
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={() => handlePublishToClient(quot, true)}
                              title="Publish with markup to client"
                            >
                              <Share2 size={14} className="mr-1" />
                              Publish
                            </Button>
                          )
                        )}
                        
                        {/* P2 NEW: Amend button (Admin only, for sent/accepted quotations) */}
                        {isAdminRole && (quot.status === 'sent' || quot.status === 'accepted') && (
                          <button
                            onClick={() => { setSelectedQuotation(quot); setAmendReason(''); setAmendDialogOpen(true); }}
                            className="p-2 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-lg"
                            title="Create amended version"
                            data-testid={`amend-quotation-${quot.id}`}
                          >
                            <Edit2 size={18} />
                          </button>
                        )}
                        
                        <button
                          onClick={() => { setSelectedQuotation(quot); setDeleteDialogOpen(true); }}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          data-testid={`delete-quotation-${quot.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl">Create Quotation</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lead (Optional)</Label>
                  <Select value={formData.lead_id} onValueChange={(v) => setFormData({ ...formData, lead_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select lead" /></SelectTrigger>
                    <SelectContent>
                      {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Project (Optional)</Label>
                  <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent>
                      {[...projects]
                        .filter(p => p.is_active !== false)
                        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                        .map((p, idx) => <SelectItem key={p.id} value={p.id}>{idx + 1}. {p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-stone-900 font-medium">Items</Label>
                <div className="space-y-3 mt-2">
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        className="col-span-5"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="col-span-2"
                      />
                      <Input
                        placeholder="Unit"
                        value={item.unit}
                        onChange={(e) => handleItemChange(idx, 'unit', e.target.value)}
                        className="col-span-1"
                      />
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={item.rate}
                        onChange={(e) => handleItemChange(idx, 'rate', parseFloat(e.target.value) || 0)}
                        className="col-span-2"
                      />
                      <span className="col-span-1 text-sm text-right">₹{item.amount?.toLocaleString()}</span>
                      <button onClick={() => handleRemoveItem(idx)} className="col-span-1 text-red-500"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={handleAddItem}>
                    <Plus size={16} className="mr-1" /> Add Item
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Tax %</Label>
                  <Input
                    type="number"
                    value={formData.tax_percentage}
                    onChange={(e) => setFormData({ ...formData, tax_percentage: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Discount %</Label>
                  <Input
                    type="number"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="bg-stone-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm"><span>Subtotal:</span><span>₹{subtotal.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span>Tax ({formData.tax_percentage}%):</span><span>₹{taxAmount.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span>Discount ({formData.discount_percentage}%):</span><span>-₹{discountAmount.toLocaleString()}</span></div>
                <div className="flex justify-between font-bold border-t pt-2"><span>Total:</span><span>₹{total.toLocaleString()}</span></div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleCreate} className="apple-button">Create Quotation</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quotation Review Panel (Side Sheet) */}
        <QuotationReview
          quotation={selectedQuotation}
          isOpen={viewDialogOpen}
          onClose={() => { setViewDialogOpen(false); setSelectedQuotation(null); }}
          onUpdate={fetchData}
        />

        {/* Delete Dialog with Reason */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteReason(''); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 size={20} />
                Permanently Delete Quotation
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>
                    This will permanently delete <strong>{selectedQuotation?.quotation_number}</strong> from:
                  </p>
                  <ul className="list-disc pl-5 text-sm text-stone-600">
                    <li>All quotation lists</li>
                    <li>Client view</li>
                    <li>Vendor view</li>
                    <li>BOQ section</li>
                  </ul>
                  <div className="pt-2">
                    <Label className="text-stone-700">Reason for deletion *</Label>
                    <Textarea
                      placeholder="e.g., Created by mistake, Duplicate, Client requested removal..."
                      className="mt-1 min-h-[80px]"
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                    />
                    <p className="text-xs text-stone-500 mt-1">Minimum 5 characters required for audit trail</p>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteReason('')}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete} 
                disabled={deleting || deleteReason.trim().length < 5}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* P2 NEW: Amend Quotation Dialog */}
        <Dialog open={amendDialogOpen} onOpenChange={setAmendDialogOpen}>
          <DialogContent className="max-w-md" data-testid="amend-quotation-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl flex items-center gap-2">
                <Edit2 className="text-purple-500" size={20} />
                Amend Quotation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedQuotation && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm font-medium text-stone-700">Quotation:</p>
                  <p className="text-stone-900 font-semibold">{selectedQuotation.quotation_number}</p>
                  <p className="text-xs text-stone-500 mt-1">
                    Current version: v{selectedQuotation.version || 1} | Total: ₹{selectedQuotation.total?.toLocaleString()}
                  </p>
                </div>
              )}
              
              <div>
                <Label className="text-stone-900">Amendment Reason (Optional)</Label>
                <Textarea
                  placeholder="Describe changes or reason for amendment..."
                  className="bg-white text-stone-900 border-stone-300 mt-1 min-h-[80px]"
                  value={amendReason}
                  onChange={(e) => setAmendReason(e.target.value)}
                  data-testid="amend-reason-input"
                />
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This will create a new version (v{(selectedQuotation?.version || 1) + 1}) of the quotation. 
                  The old version will be marked as superseded. Client will only see the latest version.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setAmendDialogOpen(false)} className="text-stone-900">
                Cancel
              </Button>
              <Button 
                onClick={handleAmend} 
                disabled={amending}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="confirm-amend-btn"
              >
                {amending ? 'Creating...' : 'Create Amended Version'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      {/* Discount Edit Dialog */}
      <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Discount</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Discount Percentage (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={discountEditValue}
                onChange={(e) => setDiscountEditValue(e.target.value)}
                data-testid="discount-edit-input"
              />
            </div>
            {discountTarget && (
              <div className="text-sm text-stone-500">
                <p>Subtotal: ₹{discountTarget.subtotal?.toLocaleString()}</p>
                <p>Discount: ₹{Math.round(discountTarget.subtotal * (parseFloat(discountEditValue) || 0) / 100).toLocaleString()}</p>
                <p className="font-medium text-stone-900">New Total: ₹{Math.round(discountTarget.subtotal + (discountTarget.tax_amount || 0) - discountTarget.subtotal * (parseFloat(discountEditValue) || 0) / 100).toLocaleString()}</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDiscountDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleDiscountSave} data-testid="save-discount-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}
